import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import posthog from 'posthog-js';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSparks } from '../../contexts/SparksContext';
import useBackgroundMusic from '../../hooks/useBackgroundMusic';
import { equipItems, formatSparks, getShop, purchaseItem } from '../../api/sparksApi';
import AddyAvatar from './AddyAvatar';
import { markShopSeen } from './shopNudge';
import { primeShop } from './useCosmeticCatalogue';
import { CELEBRATION_MS, cosmeticIconUrl, isRigAssetId, sparksToGo, withPreview } from './cosmetics';
import { Button } from '../shadcn/button';
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../shadcn/alert-dialog';
import './sparks.css';

const SLOTS = [
  { key: 'hat', label: 'Hats' },
  { key: 'glasses', label: 'Glasses' },
  { key: 'rightHand', label: 'Right hand' },
  { key: 'leftHand', label: 'Left hand' },
  { key: 'shoes', label: 'Shoes' },
  { key: 'trousers', label: 'Trousers' },
  { key: 'back', label: 'Back Gear' },
  { key: 'skin', label: 'Skins' },
  { key: 'celebration', label: 'Celebrations' },
];

const TIER_STYLE = {
  default: 'bg-gray-100 text-gray-600',
  common: 'bg-green-100 text-green-800',
  uncommon: 'bg-blue-100 text-blue-800',
  rare: 'bg-purple-100 text-purple-800',
  premium: 'bg-yellow-100 text-yellow-800',
  exclusive: 'bg-pink-100 text-pink-800',
};

const buyLabel = (item) => (!item.affordable ? 'Keep earning' : item.price > 0 ? 'Buy' : 'Get it');

function gapCopy(item, balance) {
  if (item.owned) return null;
  if (item.exclusive) return `Earned by: ${item.achievementLabel || 'a special achievement'}`;
  if (item.affordable) return item.price > 0 ? 'You can buy this now!' : 'Free for now!';
  return `${formatSparks(sparksToGo(item, balance))} Sparks to go ✨`;
}

export default function Shop() {
  const { loggedInChild } = useAuth();
  const sparks = useSparks();
  const childId = loggedInChild?._id;
  const [shop, setShop] = useState(null);
  const [slot, setSlot] = useState('hat');
  const [busy, setBusy] = useState(null);
  const [message, setMessage] = useState(null);
  // Key of the unowned item currently tried on Addy in the sidebar. Local only; nothing is saved.
  const [previewKey, setPreviewKey] = useState(null);
  const [showingOff, setShowingOff] = useState(false);
  const showOffTimer = useRef(null);
  // The item just bought; its card moves to the top of the tab, so it is highlighted and scrolled into view.
  const [justBoughtKey, setJustBoughtKey] = useState(null);
  const justBoughtCard = useRef(null);
  // The item whose Buy button was pressed, waiting for the child to confirm. Free items skip the question.
  const [confirming, setConfirming] = useState(null);
  const music = useBackgroundMusic(Boolean(shop?.enabled));

  const load = useCallback(async () => {
    if (!childId) return;
    try {
      const data = await getShop(childId);
      setShop(data);
      primeShop(childId, data);
      markShopSeen(childId, data.balance); // clears the nav tab's count
      sparks.setBalance(data.balance);
      sparks.setEquipped(data.equipped);
    } catch (err) {
      setMessage({ kind: 'error', text: 'Could not load the shop right now.' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childId]);

  useEffect(() => {
    load();
    posthog.capture('shop_opened');
  }, [load]);

  useEffect(() => () => clearTimeout(showOffTimer.current), []);

  useEffect(() => {
    justBoughtCard.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [justBoughtKey]);

  // The child's own items lead each tab so a purchase lands at the top, ready to wear; the API's price order is kept
  // within the owned and not-owned groups (Array.prototype.sort is stable).
  const items = useMemo(
    () => (shop?.items || []).filter((i) => i.slot === slot).sort((a, b) => Number(b.owned) - Number(a.owned)),
    [shop, slot],
  );
  // Re-resolved from the latest shop payload so the preview's price/affordability never go stale.
  const preview = useMemo(() => (shop?.items || []).find((i) => i.key === previewKey && !i.owned) || null, [shop, previewKey]);
  const shownEquipped = useMemo(() => withPreview(shop?.equipped, preview), [shop, preview]);

  // Addy shows a new item off with his celebration (the equipped one, or the one being tried on). Off for a
  // moment first so a second item in quick succession restarts the clip rather than leaving it running.
  const showOff = () => {
    clearTimeout(showOffTimer.current);
    setShowingOff(false);
    showOffTimer.current = setTimeout(() => {
      setShowingOff(true);
      showOffTimer.current = setTimeout(() => setShowingOff(false), CELEBRATION_MS);
    }, 60);
  };

  const tryOn = (item) => {
    if (previewKey === item.key) {
      setPreviewKey(null);
      return;
    }
    setPreviewKey(item.key);
    showOff();
    posthog.capture('shop_item_previewed', { itemKey: item.key, tier: item.tier, affordable: item.affordable });
  };

  const buy = async (item) => {
    setBusy(item.key);
    setMessage(null);
    try {
      const r = await purchaseItem(childId, item.key);
      posthog.capture('shop_item_purchased', { itemKey: item.key, tier: item.tier, pricePaid: r.pricePaid, balanceAfter: r.balance });
      setMessage({ kind: 'ok', text: `${item.name} is yours! Tap "Wear it" to put it on Addy.` });
      setPreviewKey((k) => (k === item.key ? null : k));
      await load();
      setSlot(item.slot);
      setJustBoughtKey(item.key);
    } catch (err) {
      const text = err?.response?.data?.message || 'That did not work — try again in a moment.';
      setMessage({ kind: 'error', text: Array.isArray(text) ? text.join(', ') : text });
    } finally {
      setBusy(null);
    }
  };

  const askToBuy = (item) => (item.price > 0 ? setConfirming(item) : buy(item));

  const confirmBuy = () => {
    const item = confirming;
    setConfirming(null);
    buy(item);
  };

  const wear = async (item, off = false) => {
    setBusy(item.key);
    setMessage(null);
    setJustBoughtKey(null);
    try {
      const r = await equipItems(childId, { [item.slot]: off ? '' : item.key });
      sparks.setEquipped(r.equipped);
      posthog.capture('addy_item_equipped', { slot: item.slot, itemKey: off ? null : item.key });
      await load();
      if (!off) {
        // Wearing an item takes off whatever was being tried on in that slot, so Addy shows what he now wears.
        if (preview?.slot === item.slot) setPreviewKey(null);
        showOff();
      }
    } catch (err) {
      const text = err?.response?.data?.message || 'Could not change Addy right now.';
      setMessage({ kind: 'error', text: Array.isArray(text) ? text.join(', ') : text });
    } finally {
      setBusy(null);
    }
  };

  if (!childId) return (
    <div className="container mx-auto p-6 text-center">
      <p className="text-[#2F1E6B] font-medium">Please log in as a child first, using the <a href="/children-dashboard" className="underline font-bold">Children Dashboard</a> tab.</p>
    </div>
  );
  if (!shop) return <div className="container mx-auto p-6 text-gray-600">Loading Addy's Shop…</div>;
  if (!shop.enabled) return <div className="container mx-auto p-6 text-gray-600">Addy's Shop is closed for a little while. Your Sparks are safe!</div>;

  return (
    <div className="container mx-auto p-4 max-w-5xl">
      <div className="flex flex-col md:flex-row gap-6">
        <aside className="md:w-64 shrink-0 flex flex-col items-center bg-white rounded-2xl border border-[#E6DDFB] p-4">
          {/* Addy plays the shown (equipped or tried-on) celebration on the Celebrations tab and after each try-on or wear. */}
          <AddyAvatar equipped={shownEquipped} catalogue={shop.items} size={200} bleed celebrating={slot === 'celebration' || showingOff} />
          <div className="mt-3 text-2xl font-bold text-[#2F1E6B]">{formatSparks(shop.balance)} <span aria-hidden>✨</span></div>
          <div className="text-xs text-gray-500">your Sparks</div>
          {preview && (
            <div className="mt-3 w-full rounded-xl bg-[#F1EEF7] p-3 text-center">
              <div className="text-xs text-gray-500">Trying on</div>
              <div className="font-semibold text-[#2F1E6B]">{preview.name}</div>
              <div className="mt-1 text-sm text-[#2F1E6B]">{gapCopy(preview, shop.balance)}</div>
              <div className="mt-2 flex flex-wrap justify-center gap-2">
                {!preview.exclusive && (
                  <Button size="sm" className="bg-[#A1CC3A] text-[#2F1E6B] hover:bg-[#8fb832]" disabled={busy === preview.key || !shop.canBuy || !preview.affordable} onClick={() => askToBuy(preview)}>
                    {buyLabel(preview)}
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => setPreviewKey(null)}>Back to my Addy</Button>
              </div>
            </div>
          )}
          {!shop.canBuy && (
            <p className="mt-3 text-xs text-center text-gray-500">Buying and dressing Addy needs an active subscription or trial. Everything you own is kept.</p>
          )}
        </aside>

        <section className="flex-1">
          <div className="flex items-center justify-between gap-2 mb-3">
            <h1 className="text-2xl font-bold text-[#2F1E6B]">Addy's Shop</h1>
            <Link to="/achievements" className="ml-auto text-sm font-semibold text-[#5C3BD1] hover:underline">🏅 Achievements</Link>
            <button
              onClick={music.toggleMuted}
              aria-pressed={!music.muted}
              className="px-3 py-1.5 rounded-full text-sm font-semibold bg-[#F1EEF7] text-[#5C3BD1] hover:bg-[#E6DDFB]"
            >
              <span aria-hidden>{music.muted ? '🔇' : '🔊'}</span> Music {music.muted ? 'off' : 'on'}
            </button>
          </div>
          <div className="flex flex-wrap gap-2 mb-4">
            {SLOTS.map((s) => (
              <button
                key={s.key}
                onClick={() => { setSlot(s.key); setJustBoughtKey(null); }}
                className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-colors ${slot === s.key ? 'bg-[#5C3BD1] text-white' : 'bg-[#F1EEF7] text-[#5C3BD1] hover:bg-[#E6DDFB]'}`}
              >
                {s.label}
              </button>
            ))}
          </div>

          {message && (
            <div className={`mb-3 rounded-lg px-3 py-2 text-sm ${message.kind === 'ok' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'}`}>{message.text}</div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {items.map((item) => {
              // Something the child can buy right now: a corner sticker and a yellow border instead of a line of text.
              const canBuyNow = !item.owned && !item.exclusive && item.affordable && item.price > 0;
              const gap = canBuyNow ? null : gapCopy(item, shop.balance);
              const isBusy = busy === item.key;
              const isPreviewing = preview?.key === item.key;
              const justBought = justBoughtKey === item.key;
              const canTryOn = !item.owned && isRigAssetId(item.riveAssetId);
              return (
                <div key={item.key} ref={justBought ? justBoughtCard : undefined} className={`relative rounded-2xl border p-3 bg-white flex flex-col gap-2 ${justBought ? 'border-[#A1CC3A] ring-2 ring-[#A1CC3A]' : item.equipped || isPreviewing ? 'border-[#5C3BD1] ring-2 ring-[#E6DDFB]' : canBuyNow ? 'border-[#FFD966] ring-1 ring-[#FFD966]' : 'border-[#E6DDFB]'}`} onMouseEnter={() => posthog.capture('shop_item_viewed', { itemKey: item.key, tier: item.tier, affordable: item.affordable })}>
                  {canBuyNow && <span className="sparks-buy-sticker sparks-buy-sticker--corner">✨ You can buy!</span>}
                  <div className="flex items-center justify-between">
                    {isRigAssetId(item.riveAssetId) ? (
                      <img className="h-12 w-12 object-contain" src={cosmeticIconUrl(item)} alt="" />
                    ) : (
                      <div className="text-3xl" aria-hidden>{item.riveAssetId || '—'}</div>
                    )}
                    <span className={`text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded-full ${TIER_STYLE[item.tier] || ''}`}>{item.tier}</span>
                  </div>
                  <div className="font-semibold text-[#2F1E6B]">{item.name}{item.animated ? ' ✦' : ''}</div>
                  {item.tier !== 'default' && !item.exclusive && (
                    <div className="text-sm text-gray-700">{item.price > 0 ? <>{formatSparks(item.price)} <span aria-hidden>✨</span></> : 'Free'}</div>
                  )}
                  {!item.owned && !item.exclusive && item.price > 0 && (
                    <div className="sparks-progress" aria-hidden><span style={{ width: `${Math.round(item.progress * 100)}%` }} /></div>
                  )}
                  {gap && <div className="text-xs text-gray-600">{gap}</div>}
                  {item.priceRiseAt && <div className="text-[11px] text-amber-700">Price goes up to {formatSparks(item.priceRiseAt.price)} on {new Date(item.priceRiseAt.effectiveAt).toLocaleDateString('en-GB')}</div>}
                  <div className="mt-auto flex gap-2">
                    {item.owned ? (
                      item.equipped ? (
                        item.tier !== 'default' && (
                          <Button size="sm" variant="outline" disabled={isBusy || !shop.canBuy} onClick={() => wear(item, true)}>Take off</Button>
                        )
                      ) : (
                        <Button size="sm" className="bg-[#5C3BD1] text-white hover:bg-[#4b2fb0]" disabled={isBusy || !shop.canBuy} onClick={() => wear(item)}>Wear it</Button>
                      )
                    ) : item.exclusive ? null : (
                      <Button size="sm" className="bg-[#A1CC3A] text-[#2F1E6B] hover:bg-[#8fb832]" disabled={isBusy || !shop.canBuy || !item.affordable} onClick={() => askToBuy(item)}>
                        {buyLabel(item)}
                      </Button>
                    )}
                    {canTryOn && (
                      <Button size="sm" variant="outline" onClick={() => tryOn(item)}>{isPreviewing ? 'Take it off' : 'Try it on'}</Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>

      <AlertDialog open={Boolean(confirming)} onOpenChange={(open) => !open && setConfirming(null)}>
        {confirming && (
          <AlertDialogContent className="max-w-sm">
            <AlertDialogHeader>
              {isRigAssetId(confirming.riveAssetId) && <img className="mx-auto h-20 w-20 object-contain" src={cosmeticIconUrl(confirming)} alt="" />}
              <AlertDialogTitle className="text-center text-[#2F1E6B]">Buy the {confirming.name}?</AlertDialogTitle>
              <AlertDialogDescription className="text-center">
                It costs {formatSparks(confirming.price)} Sparks. You will have {formatSparks(shop.balance - confirming.price)} Sparks left.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="gap-2 sm:justify-center">
              <Button variant="outline" onClick={() => setConfirming(null)}>Not yet</Button>
              <Button className="bg-[#A1CC3A] text-[#2F1E6B] hover:bg-[#8fb832]" onClick={confirmBuy}>Yes, buy it!</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        )}
      </AlertDialog>
    </div>
  );
}
